import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FeatureComparisonTable = ({ plans }) => {
  const [expandedCategories, setExpandedCategories] = useState(['core']);

  const featureCategories = [
    {
      id: 'core',
      name: 'Core Features',
      features: [
        { name: 'Job Postings', key: 'jobPostings' },
        { name: 'Candidate Visibility', key: 'candidateVisibility' },
        { name: 'Application Management', key: 'applicationManagement' },
        { name: 'Analytics Dashboard', key: 'analyticsDashboard' }
      ]
    },
    {
      id: 'advanced',
      name: 'Advanced Features',
      features: [
        { name: 'Candidate Matching Algorithm', key: 'candidateMatching' },
        { name: 'Priority Listing Placement', key: 'priorityListing' },
        { name: 'Advanced Analytics & Reports', key: 'advancedAnalytics' },
        { name: 'Bulk Actions & Automation', key: 'bulkActions' }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise Features',
      features: [
        { name: 'API Access', key: 'apiAccess' },
        { name: 'Dedicated Account Manager', key: 'accountManager' },
        { name: 'White-label Branding', key: 'whiteLabel' },
        { name: 'SLA Guarantee', key: 'slaGuarantee' }
      ]
    },
    {
      id: 'support',
      name: 'Support & Services',
      features: [
        { name: 'Email Support', key: 'emailSupport' },
        { name: 'Priority Support', key: 'prioritySupport' },
        { name: 'Phone Support', key: 'phoneSupport' },
        { name: 'Onboarding Assistance', key: 'onboarding' }
      ]
    }
  ];

  const getFeatureValue = (plan, featureKey) => {
    const featureMap = {
      basic: {
        jobPostings: '5/month',
        candidateVisibility: 'Standard',
        applicationManagement: 'Unlimited',
        analyticsDashboard: 'Basic',
        candidateMatching: 'Basic',
        priorityListing: false,
        advancedAnalytics: false,
        bulkActions: false,
        apiAccess: false,
        accountManager: false,
        whiteLabel: false,
        slaGuarantee: false,
        emailSupport: '48h response',
        prioritySupport: false,
        phoneSupport: false,
        onboarding: 'Self-service'
      },
      professional: {
        jobPostings: 'Unlimited',
        candidateVisibility: 'Enhanced',
        applicationManagement: 'Unlimited',
        analyticsDashboard: 'Advanced',
        candidateMatching: 'Enhanced AI',
        priorityListing: 'Top 3',
        advancedAnalytics: true,
        bulkActions: true,
        apiAccess: false,
        accountManager: false,
        whiteLabel: false,
        slaGuarantee: false,
        emailSupport: '24h response',
        prioritySupport: false,
        phoneSupport: false,
        onboarding: 'Guided setup'
      },
      enterprise: {
        jobPostings: 'Unlimited',
        candidateVisibility: 'Premium',
        applicationManagement: 'Unlimited',
        analyticsDashboard: 'Enterprise',
        candidateMatching: 'Premium AI',
        priorityListing: 'Guaranteed top',
        advancedAnalytics: true,
        bulkActions: 'Advanced',
        apiAccess: 'Full REST API',
        accountManager: true,
        whiteLabel: true,
        slaGuarantee: '99.9% uptime',
        emailSupport: '4h response',
        prioritySupport: true,
        phoneSupport: true,
        onboarding: 'Dedicated team'
      }
    };

    return featureMap?.[plan?.id]?.[featureKey];
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
      prev?.includes(categoryId)
        ? prev?.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const renderFeatureValue = (value) => {
    if (typeof value === 'boolean') {
      return (
        <div className="flex justify-center">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center ${
              value ? 'bg-success/10' : 'bg-muted'
            }`}
          >
            <Icon
              name={value ? 'Check' : 'X'}
              size={14}
              color={value ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
            />
          </div>
        </div>
      );
    }
    return (
      <span className="text-sm text-foreground font-medium">{value}</span>
    );
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm-sm overflow-hidden">
      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left p-4 font-semibold text-foreground">
                Features
              </th>
              {plans?.map((plan) => (
                <th key={plan?.id} className="text-center p-4">
                  <div className="font-heading font-bold text-foreground text-lg">
                    {plan?.name}
                  </div>
                  {plan?.recommended && (
                    <div className="inline-flex items-center gap-1 bg-accent/10 text-accent px-2 py-0.5 rounded-full text-xs font-medium mt-1">
                      <Icon name="Star" size={10} />
                      <span>Popular</span>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureCategories?.map((category) => (
              <React.Fragment key={category?.id}>
                <tr className="bg-muted/30 border-b border-border">
                  <td colSpan={plans?.length + 1} className="p-0">
                    <button
                      onClick={() => toggleCategory(category?.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    >
                      <span className="font-semibold text-foreground flex items-center gap-2">
                        <Icon name="Folder" size={16} className="text-accent" />
                        {category?.name}
                      </span>
                      <Icon
                        name={expandedCategories?.includes(category?.id) ? 'ChevronUp' : 'ChevronDown'}
                        size={18}
                        className="text-muted-foreground"
                      />
                    </button>
                  </td>
                </tr>
                {expandedCategories?.includes(category?.id) &&
                  category?.features?.map((feature, idx) => (
                    <tr
                      key={feature?.key}
                      className={`border-b border-border hover:bg-muted/20 transition-colors ${
                        idx === category?.features?.length - 1 ? 'border-b-2' : ''
                      }`}
                    >
                      <td className="p-4 text-sm text-muted-foreground">
                        {feature?.name}
                      </td>
                      {plans?.map((plan) => (
                        <td key={plan?.id} className="p-4 text-center">
                          {renderFeatureValue(getFeatureValue(plan, feature?.key))}
                        </td>
                      ))}
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile View */}
      <div className="md:hidden">
        {featureCategories?.map((category) => (
          <div key={category?.id} className="border-b border-border last:border-b-0">
            <button
              onClick={() => toggleCategory(category?.id)}
              className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <span className="font-semibold text-foreground flex items-center gap-2">
                <Icon name="Folder" size={16} className="text-accent" />
                {category?.name}
              </span>
              <Icon
                name={expandedCategories?.includes(category?.id) ? 'ChevronUp' : 'ChevronDown'}
                size={18}
                className="text-muted-foreground"
              />
            </button>
            {expandedCategories?.includes(category?.id) && (
              <div className="p-4 space-y-4">
                {category?.features?.map((feature) => (
                  <div key={feature?.key} className="space-y-2">
                    <p className="text-sm font-medium text-foreground">{feature?.name}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {plans?.map((plan) => (
                        <div key={plan?.id} className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">{plan?.name}</p>
                          {renderFeatureValue(getFeatureValue(plan, feature?.key))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeatureComparisonTable;